import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const params = new URLSearchParams(hash.substring(1));
    const sessionId = params.get('session_id');

    // Immediately clean the hash from URL to prevent stale session_id reuse
    if (window.history.replaceState) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }

    if (!sessionId) {
      navigate('/admin', { replace: true });
      return;
    }

    exchangeSession(sessionId);
  }, []);

  const exchangeSession = async (sessionId) => {
    try {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const apiBase = window.location.origin + '/api';
      const response = await axios.post(
        `${apiBase}/auth/session`,
        { session_id: sessionId },
        { withCredentials: true, timeout: 30000 }
      );
      
      if (response.data.session_token) {
        localStorage.setItem('admin_token', response.data.session_token);
      }
      
      navigate('/admin/dashboard', { state: { user: response.data.user }, replace: true });
    } catch (err) {
      console.error('Session exchange failed:', err?.response?.data || err.message);
      setError(err?.response?.data?.detail || 'Authentication failed. Please try again.');
    }
  };

  if (error) {
    return (
      <div data-testid="auth-callback-error" className="min-h-screen flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-destructive" />
          <h2 className="text-xl font-bold mb-2">Sign-in Failed</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button
            data-testid="retry-login-btn"
            className="btn-primary"
            onClick={() => navigate('/admin', { replace: true })}
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="auth-callback-screen" className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-16 h-16 mx-auto mb-4 animate-spin text-primary" />
        <p className="text-lg text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
